/**
 * Workspace role permissions — the single source of truth for what each
 * workspace role can do.
 *
 * This module is **prescriptive**: the backend enforces it. Route middleware
 * (`requireCapability` in `packages/backend/src/auth/middleware.ts`) resolves a
 * capability to its minimum role through `minimumRoleForCapability` and rejects
 * lower roles with a 403 before the handler runs. The frontend reads the same
 * matrix (via `can`) to render honest gates. Changing a capability's tier here
 * changes enforcement everywhere.
 *
 * Enforcement uses two tiers:
 *
 * 1. **Role-gated** — routes that call `requireCapability("<capability>")`
 *    (or, for conditional in-handler checks, `can(auth.role, "<capability>")` /
 *    `hasRequiredWorkspaceRole`). Every admin/owner capability below maps to at
 *    least one such call site — the correspondence is asserted by
 *    `packages/backend/test/permissions.enforcement.test.ts`.
 *
 * 2. **Authenticated-member** — routes guarded only by `requireApiAuth`
 *    (which proves workspace membership) and then `getRequiredAuthContext`.
 *    Any authenticated member of the workspace can perform these actions; the
 *    backend does not distinguish owner/admin/member among them. Finer-grained
 *    ownership checks (e.g. `canManageDesk` for archive/pause/resume, page
 *    creator-or-admin archival, or contributor-scoped source-binding approval)
 *    apply *within* this tier but are not role-based.
 *
 * The member capabilities below are tier 2 — they describe what an
 * authenticated member *can* do today, not what the backend *rejects* a lower
 * role for. There is no `requireWorkspaceRole("member")` gate on them.
 *
 * Hierarchy: owner ⊇ admin ⊇ member. Capabilities are monotonic — every
 * capability a lower role has, the higher role also has.
 */
/**
 * Every capability the backend recognizes. Capabilities are grouped by the
 * enforcement tier that guards them:
 *
 * - **Authenticated-member** (tier 2): guarded by `requireApiAuth` +
 *   `getRequiredAuthContext` only — any workspace member can perform them.
 *   No `requireWorkspaceRole("member")` gate exists; the backend does not
 *   actively reject a lower role (there is no role below member that has API
 *   access). Finer-grained ownership checks may apply within the tier.
 * - **Role-gated admin** (tier 1): guarded by `requireCapability` resolving to
 *   the admin role.
 * - **Role-gated owner** (tier 1): guarded by `requireCapability` resolving to
 *   the owner role (or an equivalent in-handler owner check).
 *
 * If you add a role-gated capability, add the backend call site and map it in
 * `packages/backend/test/permissions.enforcement.test.ts` — the test fails
 * until matrix and enforcement correspond. Authenticated-member capabilities
 * need no role gate (any member can do them), but the action's route must
 * still exist.
 */
export const WORKSPACE_CAPABILITIES = [
    // authenticated-member: available to any workspace member (tier 2)
    "read",
    "comment",
    "use-drafts",
    "manage-team-lifecycle",
    "approve-source-bindings",
    "resolve-decisions",
    "view-pulse",
    "view-own-usage",
    "create-team",
    // role-gated admin (tier 1)
    "view-usage",
    "view-billing",
    "invite-members",
    "manage-members",
    "manage-connections",
    "manage-playbook",
    "approve-pending-actions",
    "manage-briefs",
    "manage-billing",
    "manage-connector-drafts",
    "manage-channels",
    "manage-repos",
    "manage-mcp-servers",
    "manage-autonomous-settings",
    "manage-webhook-subscriptions",
    "manage-guardrails",
    "manage-workers",
    "manage-code-tokens",
    "manage-otel-export",
    "view-sso-settings",
    // role-gated owner (tier 1)
    "manage-owner-roles",
    "manage-workspace-settings",
    "view-draft-quality",
    "manage-sso",
];
// create-team moved here 2026-09-23: any member may start a Team; adding people stays manage-members.
const MEMBER_CAPABILITIES = [
    "read",
    "comment",
    "use-drafts",
    "manage-team-lifecycle",
    "approve-source-bindings",
    "resolve-decisions",
    "view-pulse",
    "view-own-usage",
    "create-team",
];
const ADMIN_CAPABILITIES = [
    ...MEMBER_CAPABILITIES,
    "view-usage",
    "view-billing",
    "invite-members",
    "manage-members",
    "manage-connections",
    "manage-playbook",
    "approve-pending-actions",
    "manage-briefs",
    "manage-billing",
    "manage-connector-drafts",
    "manage-channels",
    "manage-repos",
    "manage-mcp-servers",
    "manage-autonomous-settings",
    "manage-webhook-subscriptions",
    "manage-guardrails",
    "manage-workers",
    "manage-code-tokens",
    "manage-otel-export",
    "view-sso-settings",
];
const OWNER_CAPABILITIES = [
    ...ADMIN_CAPABILITIES,
    "manage-owner-roles",
    "manage-workspace-settings",
    "view-draft-quality",
    "manage-sso",
];
/**
 * The permission matrix: what each workspace role can do.
 *
 * SSO is split across two tiers on purpose: admins may *read* the workspace's
 * SAML connection (`view-sso-settings`, so they can see how sign-in is wired),
 * but only owners may create, change, or delete it (`manage-sso`). SSO config
 * is auth surface — a compromised admin must not be able to point the
 * workspace at an attacker-controlled IdP.
 *
 * - `member`: read workspace content, comment, use drafts, work within Teams
 *   (lifecycle actions as a delegated editor), approve personal source
 *   bindings, resolve decisions in their own conversations, view pulse,
 *   see their own usage/spend, and create Teams. These are authenticated-member capabilities
 *   (tier 2): any workspace member can do them; the backend does not role-gate
 *   them further.
 * - `admin`: member + view workspace usage and billing, manage
 *   members, connections, playbook, pending actions, triggers, briefs,
 *   billing, connector drafts, channels, repos, MCP servers, custom skills,
 *   autonomous settings, webhook subscriptions, CRM, guardrails, workspace
 *   email sends, workers, folder deletion, pulse curation, code tokens, and
 *   read the SSO connection.
 *   (Feature flags are NOT a workspace capability at any tier — they are
 *   platform rollout gates, managed only through the super-admin God Mode
 *   surface. Personal access tokens are member-tier: any member may mint a
 *   full-access PAT, which is capped at the minter's own role.)
 * - `owner`: admin + manage owner roles, workspace settings, view draft
 *   quality, and manage SSO.
 */
export const WORKSPACE_ROLE_PERMISSIONS = {
    member: {
        label: "Member",
        summary: "Read workspace content, comment, use drafts, work within Teams, approve source bindings, resolve decisions, view pulse, see their own usage, and create Teams.",
        capabilities: MEMBER_CAPABILITIES,
    },
    admin: {
        label: "Admin",
        summary: "Everything a Member can do, plus manage members, connections, integrations, approvals, billing and usage, guardrails, email, workers, and CRM, and view single sign-on settings.",
        capabilities: ADMIN_CAPABILITIES,
    },
    owner: {
        label: "Owner",
        summary: "Everything an Admin can do, plus manage owner roles, workspace settings, view draft quality, and configure single sign-on.",
        capabilities: OWNER_CAPABILITIES,
    },
};
/** Role hierarchy, lowest to highest. */
export const WORKSPACE_ROLE_ORDER = [
    "member",
    "admin",
    "owner",
];
const CAPABILITY_SETS = {
    member: new Set(WORKSPACE_ROLE_PERMISSIONS.member.capabilities),
    admin: new Set(WORKSPACE_ROLE_PERMISSIONS.admin.capabilities),
    owner: new Set(WORKSPACE_ROLE_PERMISSIONS.owner.capabilities),
};
/** Whether `role` has `capability` under the matrix. */
export function can(role, capability) {
    return CAPABILITY_SETS[role].has(capability);
}
/**
 * The lowest role that has `capability`. Because the matrix is monotonic,
 * every role at or above the returned role has it too.
 */
export function minimumRoleForCapability(capability) {
    for (const role of WORKSPACE_ROLE_ORDER) {
        if (CAPABILITY_SETS[role].has(capability)) {
            return role;
        }
    }
    // Unreachable while the matrix covers WORKSPACE_CAPABILITIES (asserted by
    // the shared permissions tests) — owner holds every capability.
    throw new Error(`Capability "${capability}" is missing from the matrix`);
}
//# sourceMappingURL=permissions.js.map